import React, { useEffect, useRef, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  Search,
  Filter,
  X,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useArticles, Article } from "@/hooks/use-articles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const ArticleCard = ({
  title,
  excerpt,
  category,
  subCategory,
  author,
  date,
  readTime,
  imageUrl,
  id,
  index,
}: Article & { index?: number }) => {
  return (
    <div
      className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-md hover:-translate-y-1"
      style={
        index !== undefined ? { animationDelay: `${0.1 + index * 0.05}s` } : {}
      }
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
        />
        <div className="absolute top-4 left-4">
          <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-medium">
            {category}
          </span>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-xl font-bold mb-2 text-gray-800">{title}</h3>
        <p className="text-gray-600 mb-4 flex-grow">{excerpt}</p>
        <div className="flex items-center justify-between text-sm text-gray-500 mt-auto">
          <span>{author}</span>
          <div className="flex items-center space-x-3">
            <span>{date}</span>
            <span>•</span>
            <span>{readTime}</span>
          </div>
        </div>
        <Link
          to={`/article?id=${id}`}
          className="mt-4 inline-flex items-center justify-center px-4 py-2 border border-primary-500 text-primary-500 rounded-md font-medium hover:bg-primary-500 bg-navy-light text-white transition-all duration-300 group"
        >
          Read More
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </Link>
      </div>
    </div>
  );
};

const Articles = () => {
  const {
    articles,
    categories,
    categoryDisplayNames,
    availableSubcategories,
    searchQuery,
    selectedCategories,
    selectedSubCategories,
    isFilterExpanded,
    loading,
    loadingMore,
    hasMore,
    error,
    handleSearchChange,
    handleCategoryChange,
    handleSubCategoryChange,
    resetFilters,
    toggleFilterPanel,
    loadMoreArticles,
  } = useArticles();

  // Ref cho last article element để phát hiện scroll
  const observer = useRef<IntersectionObserver | null>(null);
  const lastArticleElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || loadingMore) return;

      // Disconnect existing observer
      if (observer.current) observer.current.disconnect();

      // Create new observer
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreArticles();
          }
        },
        { threshold: 0.5 }
      );

      // Observe new node if exists
      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, loadMoreArticles]
  );

  // Set page title based on category
  useEffect(() => {
    let pageTitle = "Articles";

    if (selectedCategories.length === 1) {
      pageTitle = categoryDisplayNames[selectedCategories[0]];
    } else if (selectedCategories.length > 1) {
      pageTitle = "Multiple Categories";
    }

    document.title = `${pageTitle} | GlowHer.Life`;
  }, [selectedCategories, categoryDisplayNames]);

  // Group subcategories by category for better dropdown organization
  const groupedSubcategories = availableSubcategories.reduce((acc, subcat) => {
    if (!acc[subcat.category]) {
      acc[subcat.category] = [];
    }
    acc[subcat.category].push(subcat);
    return acc;
  }, {} as Record<string, typeof availableSubcategories>);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Hero section */}
        <section className="relative py-16 md:py-24 bg-[#1A2B4D] overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

          <div className="container mx-auto px-4 md:px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              <span
                className="inline-block px-3 py-1 bg-white/10 text-white text-sm rounded-full mb-4 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.1s" }}
              >
                {selectedCategories.length === 1
                  ? categoryDisplayNames[selectedCategories[0]]
                  : selectedCategories.length > 1
                  ? "Multiple Categories"
                  : "All Articles"}
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Discover Our Latest Articles
              </h1>
              <p
                className="text-xl text-white/90 leading-relaxed animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                Explore our collection of articles crafted to inspire, inform,
                and elevate your lifestyle
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter */}
        <section className="py-8 bg-gray-50 sticky top-16 z-10 border-b border-gray-200 shadow-sm">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-4">
              {/* Search and filter controls */}
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="relative w-full md:w-96">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search articles..."
                    className="pl-10 bg-white"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                <div className="flex gap-2 w-full md:w-auto justify-between md:justify-end">
                  {/* Category filter dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex-1 md:flex-none">
                        <Filter className="mr-2 h-4 w-4" />
                        Categories
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-[70vh] overflow-auto">
                      <DropdownMenuLabel>Select Categories</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {categories.map((category) => (
                          <DropdownMenuItem
                            key={category.id}
                            className={`cursor-pointer flex items-center justify-between ${
                              selectedCategories.includes(category.id)
                                ? "bg-gray-100"
                                : ""
                            }`}
                            onClick={() =>
                              handleCategoryChange(
                                category.id,
                                !selectedCategories.includes(category.id)
                              )
                            }
                          >
                            {category.name}
                            {selectedCategories.includes(category.id) && (
                              <X className="h-4 w-4 text-gray-500" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Subcategory filter dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex-1 md:flex-none">
                        <Filter className="mr-2 h-4 w-4" />
                        Subcategories
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-[70vh] overflow-auto">
                      <DropdownMenuLabel>
                        Select Subcategories
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.entries(groupedSubcategories).map(
                        ([categoryId, subcats]) => (
                          <React.Fragment key={categoryId}>
                            <DropdownMenuLabel className="text-xs text-gray-500 px-2 py-1">
                              {categoryDisplayNames[categoryId]}
                            </DropdownMenuLabel>
                            <DropdownMenuGroup>
                              {subcats.map((subCategory) => (
                                <DropdownMenuItem
                                  key={`${subCategory.category}-${subCategory.id}`}
                                  className={`cursor-pointer flex items-center justify-between ${
                                    selectedSubCategories.includes(
                                      subCategory.slug
                                    )
                                      ? "bg-gray-100"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleSubCategoryChange(
                                      subCategory.slug,
                                      !selectedSubCategories.includes(
                                        subCategory.slug
                                      )
                                    )
                                  }
                                >
                                  {subCategory.name}
                                  {selectedSubCategories.includes(
                                    subCategory.slug
                                  ) && <X className="h-4 w-4 text-gray-500" />}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                          </React.Fragment>
                        )
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Reset filters button */}
                  {(selectedCategories.length > 0 ||
                    selectedSubCategories.length > 0 ||
                    searchQuery.trim() !== "") && (
                    <Button
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50 flex-1 md:flex-none"
                      onClick={resetFilters}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Active filters display */}
              {!loading &&
                !error &&
                (selectedCategories.length > 0 ||
                  selectedSubCategories.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedCategories.map((categoryId) => (
                      <div
                        key={categoryId}
                        className="bg-[#1A2B4D] text-white text-xs rounded-full px-3 py-1.5 flex items-center"
                      >
                        {categoryDisplayNames[categoryId]}
                        <button
                          className="ml-2 hover:opacity-80"
                          onClick={() =>
                            handleCategoryChange(categoryId, false)
                          }
                          aria-label={`Remove ${categoryDisplayNames[categoryId]} filter`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {selectedSubCategories.map((subCategoryId) => {
                      // Find the subcategory object
                      const subcat = availableSubcategories.find(
                        (sc) => sc.slug === subCategoryId
                      );

                      if (!subcat) return null;

                      return (
                        <div
                          key={subCategoryId}
                          className="bg-gold text-[#1A2B4D] text-xs rounded-full px-3 py-1.5 flex items-center"
                        >
                          {subcat.name}
                          <button
                            className="ml-2 hover:opacity-80"
                            onClick={() =>
                              handleSubCategoryChange(subCategoryId, false)
                            }
                            aria-label={`Remove ${subcat.name} filter`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            {loading && !articles.length ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  Loading articles...
                </h3>
                <p className="text-[#1A2B4D]/70">
                  Please wait while we load the articles.
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">!</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  An error occurred
                </h3>
                <p className="text-[#1A2B4D]/70">{error}</p>
                <Button
                  className="mt-4"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </Button>
              </div>
            ) : articles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles.map((article, index) => {
                    // If it's the last article, add a ref to detect scroll
                    if (articles.length === index + 1) {
                      return (
                        <div
                          ref={lastArticleElementRef}
                          key={article.id}
                          className="animate-fade-in opacity-0"
                          style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                        >
                          <ArticleCard {...article} index={index} />
                          {/* Debug categoryId */}
                          {/* <div className="text-xs text-red-500 bg-yellow-100 p-1 rounded mt-1 mb-2">
                            Debug: CategoryID: {article.categoryId}
                          </div> */}
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={article.id}
                          className="animate-fade-in opacity-0"
                          style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                        >
                          <ArticleCard {...article} index={index} />
                          {/* Debug categoryId */}
                          {/* <div className="text-xs text-red-500 bg-yellow-100 p-1 rounded mt-1 mb-2">
                            Debug: CategoryID: {article.categoryId}
                          </div> */}
                        </div>
                      );
                    }
                  })}
                </div>

                {/* Loading spinner when loading more */}
                {loadingMore && (
                  <div className="text-center py-8 mt-8">
                    <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#1A2B4D]/70">
                      Loading more articles...
                    </p>
                  </div>
                )}

                {/* Notice when all articles are loaded */}
                {!hasMore && articles.length > 5 && (
                  <div className="text-center py-8 mt-8">
                    <p className="text-[#1A2B4D]/70">
                      All articles have been displayed
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  No articles found
                </h3>
                <p className="text-[#1A2B4D]/70">
                  Try changing your search criteria or check back later for new
                  content.
                </p>
                {(selectedCategories.length > 0 ||
                  selectedSubCategories.length > 0 ||
                  searchQuery) && (
                  <Button
                    className="mt-4 transition-all duration-300 hover:scale-105"
                    onClick={resetFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Articles;
